import { Carousel as CarouselUI } from "@repo/ui-parkmobile/Carousel";
import type {
  Carousel as CarouselType,
  CardAlignable,
  CardLogo,
  CardLocations,
  CardCity,
  CardParking,
  FeatureItem,
  SimpleRichText as SimpleRichTextType,
} from "@/generated/sb";
import {
  sectionHeaderResolver,
  titleResolver,
  textResolver,
  imageStoryblokResolver,
  universalLinkResolver,
  buttonResolver,
} from "@/lib/storyblok/adaptors";
import { storyblokEditable } from "@storyblok/react/rsc";
import { SimpleRichText } from "@/components/SimpleRichText";
import { CardAlignableProps } from "@repo/ui-parkmobile/CardAlignable";
import { LogoProps } from "@repo/ui-parkmobile/Logo";
import { CardLocationsProps } from "@repo/ui-parkmobile/CardLocations";
import { CardCityProps } from "@repo/ui-parkmobile/CardCity";
import {
  CardParkingProps,
  CardParkingFeature,
} from "@repo/ui-parkmobile/CardParking";
import {
  trackLogoCarouselClick,
  trackCarouselNavigation,
  trackStepInteraction,
  trackCheckAvailabilityClick,
  trackCarouselNavigationClick,
  trackDestinationCardClick,
} from "@/utils/trackingScripts";
import { extractTextFromRichtext } from "@/utils/analytics";

interface CarouselProps {
  blok: CarouselType;
  pageType?: "default" | "location";
}

function cardAlignableResolver(
  blok?: CardAlignable,
): CardAlignableProps | null {
  if (!blok) {
    return null;
  }

  const iconImage = blok.icon?.[0];
  const icon = imageStoryblokResolver(iconImage);

  const title = titleResolver(blok.title);
  const text = textResolver(blok.text);

  const links =
    blok.link
      ?.map((linkBlok) => {
        const linkData = universalLinkResolver(linkBlok.link);
        return linkData
          ? {
              text: linkBlok.text || "",
              href: String(linkData.href || "#"),
              target: linkData.target,
            }
          : null;
      })
      .filter((link): link is NonNullable<typeof link> => link !== null) ||
    null;

  return {
    icon,
    iconSize: blok.iconSize || "small",
    title: title || null,
    text: text || null,
    links: links && links.length > 0 ? links : null,
    backgroundColor: blok.backgroundColor || "primary",
    aligning: blok.aligning || "left",
    verticalPadding: blok.verticalPadding || "default",
    horizontalPadding: blok.horizontalPadding || "default",
  };
}

function cardLocationsResolver(
  blok?: CardLocations,
): CardLocationsProps | null {
  if (!blok) {
    return null;
  }

  const imageBlok = blok.image?.[0];
  const image = imageStoryblokResolver(imageBlok);

  const linkData = universalLinkResolver(blok.link);
  const link = linkData
    ? {
        text: blok.title || "",
        href: String(linkData.href || "#"),
        target: linkData.target,
      }
    : null;

  return {
    image,
    tariff: blok.tariff,
    title: blok.title,
    text: blok.text,
    link,
  };
}

function cardCityResolver(blok?: CardCity): CardCityProps | null {
  if (!blok) {
    return null;
  }

  const imageBlok = blok.image?.[0];
  const image = imageStoryblokResolver(imageBlok);

  const linkData = universalLinkResolver(blok.link);
  const link = linkData
    ? {
        text: blok.title || "",
        href: String(linkData.href || "#"),
        target: linkData.target,
      }
    : null;

  return {
    image,
    title: blok.title,
    link,
  };
}

function cardParkingResolver(
  blok?: CardParking,
): CardParkingProps | null {
  if (!blok) {
    return null;
  }

  const imageBlok = blok.image?.[0];
  const image = imageStoryblokResolver(imageBlok);

  const description = blok.description ? (
    <SimpleRichText
      blok={
        {
          component: "simpleRichText",
          _uid: `${blok._uid}_description`,
          content: blok.description,
          size: "small",
          fontColor: "inverted",
        } as SimpleRichTextType
      }
    />
  ) : undefined;

  const features: CardParkingFeature[] | undefined = blok.features
    ?.map((featureBlok: FeatureItem) => {
      const icon = featureBlok.icon
        ? {
            src: featureBlok.icon.filename || "",
            alt: featureBlok.icon.alt || "",
          }
        : null;

      return {
        icon,
        text: featureBlok.text,
      };
    })
    .filter(Boolean);

  // Through buttonResolver so link, anchor and Navattic buttons all work here;
  // the card keeps its own variant/size defaults.
  const buttonBlok = blok.button?.[0];
  const resolvedButton = buttonBlok ? buttonResolver(buttonBlok) : null;
  const button = resolvedButton
    ? {
        ...resolvedButton,
        text: resolvedButton.text || "",
        variant: buttonBlok?.variant || "inverted-fill",
        size: buttonBlok?.size || "small",
      }
    : null;

  const linkData = universalLinkResolver(blok.link);
  const link = linkData
    ? {
        text: blok.title || "",
        href: String(linkData.href || "#"),
        target: linkData.target,
      }
    : null;

  return {
    image,
    tariff: blok.tariff,
    title: blok.title,
    description,
    features,
    button,
    link,
  };
}

export default function Carousel({
  blok,
  pageType = "default",
}: CarouselProps) {
  if (!blok) {
    return null;
  }

  // Resolve header using SectionHeader
  const header = blok.typography
    ? sectionHeaderResolver(blok.typography)
    : null;

  const sectionName =
    header?.title?.content ||
    blok.typography?.[0]?.title?.content ||
    "Payment integrations";

  const cardsMetadata: Array<{
    logoName?: string;
    contentDescription?: string;
    parkingName?: string;
    price?: string;
    distance?: string;
    locationName?: string;
    cardType?: string;
  }> = [];
  const cards: (
    | CardAlignableProps
    | LogoProps
    | CardLocationsProps
    | CardCityProps
    | CardParkingProps
  )[] = [];

  if (blok.cards) {
    blok.cards.forEach((cardBlok, index) => {
      if (cardBlok.component === "cardAlignable") {
        const alignableCard = cardBlok as CardAlignable;
        const card = cardAlignableResolver(alignableCard);
        if (card) {
          const isStepCard = alignableCard.type === "step";
          const rawTitle = alignableCard.title?.[0]?.content || "";
          const rawText = extractTextFromRichtext(
            alignableCard.text?.[0]?.content,
          );

          const cardClickHandler = isStepCard
            ? () => {
                trackStepInteraction({
                  section: sectionName,
                  config: "cardAlignable",
                  stepNumber: index + 1,
                  stepTitle: rawTitle,
                  stepDescription: rawText,
                  interactionType: "click",
                });
              }
            : undefined;

          // const cardHoverHandler = isStepCard
          //   ? () => {
          //       trackStepInteraction({
          //         section: sectionName,
          //         config: "cardAlignable",
          //         stepNumber: index + 1,
          //         stepTitle: rawTitle,
          //         stepDescription: rawText,
          //         interactionType: "hover",
          //       });
          //     }
          //   : undefined;

          cards.push({
            ...card,
            onCardClick: cardClickHandler,
            // onCardHover: cardHoverHandler,
            ...storyblokEditable(cardBlok),
          });
          cardsMetadata.push({
            contentDescription: card.text?.children || "",
          });
        }
      } else if (cardBlok.component === "cardLogo") {
        const logoBlok = cardBlok as CardLogo;
        const imageBlok = logoBlok.image?.[0];
        const imageAsset = imageStoryblokResolver(imageBlok);

        const logoName = logoBlok.logoName || imageAsset?.alt || "";

        const logo: LogoProps = {
          image: imageAsset,
          backgroundColor: logoBlok.backgroundColor || "default",
          onClick: () => {
            trackLogoCarouselClick({
              section: sectionName,
              config: "logoCarousel",
              logoName,
            });
          },
          ...storyblokEditable(logoBlok),
        };
        cards.push(logo);
        cardsMetadata.push({
          logoName,
          contentDescription: imageAsset?.alt || "",
        });
      } else if (cardBlok.component === "cardLocations") {
        const locationsBlok = cardBlok as CardLocations;
        const card = cardLocationsResolver(locationsBlok);
        if (card) {
          const destinationName = card.title || "";
          const price = card.tariff || "";
          const contentDescription = card.text || "";

          cards.push({
            ...card,
            onClick: () => {
              trackDestinationCardClick({
                section: sectionName,
                config: "cardLocation",
                destinationName,
                price,
                contentDescription,
              });
            },
            ...storyblokEditable(cardBlok),
          });
          cardsMetadata.push({
            cardType: "cardLocations",
            locationName: destinationName,
            price,
            contentDescription,
          });
        }
      } else if (cardBlok.component === "cardCity") {
        const cityBlok = cardBlok as CardCity;
        const card = cardCityResolver(cityBlok);
        if (card) {
          const destinationName = card.title || "";
          const price = ""; // City cards don't have price
          const contentDescription = cityBlok.text || "";

          cards.push({
            ...card,
            onClick: () => {
              trackDestinationCardClick({
                section: sectionName,
                config: "cardLocation",
                destinationName,
                price,
                contentDescription,
              });
            },
            ...storyblokEditable(cityBlok),
          });
          cardsMetadata.push({
            cardType: "cardCity",
            locationName: destinationName,
            contentDescription,
          });
        }
      } else if (cardBlok.component === "cardParking") {
        const parkingBlok = cardBlok as CardParking;
        const card = cardParkingResolver(parkingBlok);
        if (card) {
          const parkingName = card.title || "";
          const price = card.tariff || "";

          const distance =
            card.features?.find((f) => f.text?.includes("min"))?.text || "";

          const enhancedButton = card.button
            ? {
                ...card.button,
                onClick: (event: React.MouseEvent) => {
                  const originalOnClick = card.button?.onClick;

                  trackCheckAvailabilityClick({
                    section: sectionName,
                    config: "cardParking",
                    ctaText: card.button?.text || "Check availability",
                    parkingName,
                    location: sectionName, // Use section name as location context
                    price,
                    distance,
                    address: parkingName,
                    contentDescription: "",
                  });

                  originalOnClick?.(event);
                },
              }
            : card.button;

          cards.push({
            ...card,
            button: enhancedButton,
            ...storyblokEditable(cardBlok),
          });
          cardsMetadata.push({
            cardType: "cardParking",
            parkingName,
            price,
            distance,
            contentDescription: "",
          });
        }
      }
    });
  }

  const handleNavigationClick = (
    direction: "left" | "right",
    slidesInView: number[],
  ) => {
    const firstCardType = cardsMetadata[0]?.cardType;

    if (firstCardType === "cardParking") {
      const activeSlide = slidesInView.map((index) => {
        const metadata = cardsMetadata[index];
        return {
          slideIndex: index + 1,
          parkingName: metadata?.parkingName || "",
          price: metadata?.price || "",
          distance: metadata?.distance || "",
          contentDescription: metadata?.contentDescription || "",
        };
      });

      trackCarouselNavigation({
        section: sectionName,
        config: "carousel",
        direction,
        activeSlide,
      });
    } else if (firstCardType === "cardLocations") {
      const activeSlide = slidesInView.map((index) => {
        const metadata = cardsMetadata[index];
        return {
          slideIndex: index + 1,
          locationName: metadata?.locationName || "",
          price: metadata?.price || "",
          contentDescription: metadata?.contentDescription || "",
        };
      });

      trackCarouselNavigationClick({
        section: sectionName,
        config: "carousel",
        direction,
        activeSlide,
      });
    } else {
      const activeSlide = slidesInView.map((index) => ({
        slideIndex: index + 1,
        logoName: cardsMetadata[index]?.logoName,
        contentDescription: cardsMetadata[index]?.contentDescription,
      }));

      trackCarouselNavigation({
        section: sectionName,
        config: "carousel",
        direction,
        activeSlide,
      });
    }
  };

  return (
    <CarouselUI
      {...storyblokEditable(blok)}
      header={header}
      cards={cards.length > 0 ? cards : undefined}
      layout={blok.layout || "default"}
      backgroundColor={blok.backgroundColor || "none"}
      topPadding={blok.topPadding || "none"}
      bottomPadding={blok.bottomPadding || "none"}
      pageType={pageType}
      onNavigationClick={handleNavigationClick}
      id={blok.anchor}
    />
  );
}
